import { createContext, useContext, useEffect, useCallback, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/auth/AuthContext';
import client from '@/api/client';

export const LANGUAGES = ['en', 'hi', 'te', 'ta', 'mr', 'kn', 'ml'] as const;
export type LanguageCode = (typeof LANGUAGES)[number];

function isLanguageCode(value: string | undefined): value is LanguageCode {
  return !!value && (LANGUAGES as readonly string[]).includes(value);
}

interface LanguageContextValue {
  setLanguage: (language: LanguageCode) => void;
}

const LanguageContext = createContext<LanguageContextValue | null>(null);

export function LanguageProvider({ children }: { children: ReactNode }) {
  const { user, updateUser } = useAuth();
  const { i18n } = useTranslation();

  useEffect(() => {
    if (isLanguageCode(user?.languagePreference) && user!.languagePreference !== i18n.language) {
      void i18n.changeLanguage(user!.languagePreference);
    }
  }, [user?.languagePreference, i18n]);

  const setLanguage = useCallback((next: LanguageCode) => {
    void i18n.changeLanguage(next);
    updateUser({ languagePreference: next });
    client.patch('/api/auth/me/language', { language: next }).catch(() => {
      // Low-stakes preference update — keep the optimistic UI even if the sync fails.
    });
  }, [i18n, updateUser]);

  return (
    <LanguageContext.Provider value={{ setLanguage }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const ctx = useContext(LanguageContext);
  if (!ctx) throw new Error('useLanguage must be used within LanguageProvider');
  return ctx;
}
